import type { AdminMetricsDto } from '@carcheck/shared';
import { OrderStatusWire } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import { tokens } from '../../../design';
import {
  DONUT_CIRCUMFERENCE,
  DONUT_RADIUS,
  buildDonutSegments,
  orderStatusColor,
  percent,
  type DonutSegment,
} from '../_lib/chartGeometry';

/**
 * Tile 2 — order-structure donut. Renders `orderStructure.byStatus` as an
 * inline-SVG donut (stroke-dasharray arcs) + a legend list with counts.
 * Mirrors the `.t-donut` tile in `design/mockups/admin/dashboard.html`.
 *
 * The centre shows the "completed" share (the mockup's 62% завершено).
 * Wire `OrderStatus` values are localised via `dashboard.orderStatus.*`
 * (rule #11) — the seven wire statuses, unknown values fall back to the
 * raw string.
 */

type Structure = AdminMetricsDto['orderStructure'];

/** Localise a wire `OrderStatus` for the donut legend. */
const useStatusLabel = (): ((status: string) => string) => {
  const { t } = useTranslation();
  return (status: string): string => {
    const key = `dashboard.orderStatus.${status}`;
    const label = t(key);
    return label === key ? status : label;
  };
};

const DonutSvg = ({
  segments,
  completedPct,
  label,
  completedLabel,
}: {
  segments: DonutSegment[];
  completedPct: number;
  label: string;
  completedLabel: string;
}): ReactElement => (
  <Box
    component="svg"
    viewBox="0 0 130 130"
    role="img"
    aria-label={label}
    sx={{ width: 120, height: 120, flexShrink: 0 }}
  >
    <circle cx="65" cy="65" r={DONUT_RADIUS} fill="none" stroke={tokens.border.default} strokeWidth="18" />
    {segments.map((seg) => (
      <circle
        key={seg.status}
        cx="65"
        cy="65"
        r={DONUT_RADIUS}
        fill="none"
        stroke={seg.color}
        strokeWidth="18"
        strokeDasharray={`${seg.dash} ${DONUT_CIRCUMFERENCE}`}
        strokeDashoffset={seg.offset}
        transform="rotate(-90 65 65)"
      />
    ))}
    <text x="65" y="62" fontFamily={tokens.font.mono} fontSize="18" fontWeight="700" fill={tokens.text.primary} textAnchor="middle">
      {completedPct}%
    </text>
    <text x="65" y="78" fontFamily={tokens.font.family} fontSize="9" fill={tokens.text.tertiary} textAnchor="middle">
      {completedLabel}
    </text>
  </Box>
);

const DonutLegend = ({
  segments,
  statusLabel,
}: {
  segments: DonutSegment[];
  statusLabel: (status: string) => string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.9, fontSize: 11, minWidth: 0 }}>
      {segments.length === 0 ? (
        <Box sx={{ color: tokens.text.tertiary }}>{t('dashboard.donut.empty')}</Box>
      ) : (
        segments.map((seg) => (
          <Box key={seg.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, background: orderStatusColor(seg.status) }} />
            <Box sx={{ flex: 1, color: tokens.text.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {statusLabel(seg.status)}
            </Box>
            <Box sx={{ fontFamily: tokens.font.mono, fontWeight: 700, color: tokens.text.primary }}>{seg.count}</Box>
          </Box>
        ))
      )}
    </Box>
  );
};

const DonutBody = ({ structure, label }: { structure: Structure; label: string }): ReactElement => {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  const segments = buildDonutSegments(structure.byStatus, structure.total);
  const completedCount = structure.byStatus.find((s) => s.status === OrderStatusWire.Completed)?.count ?? 0;
  const completedPct = percent(completedCount, structure.total);

  return (
    <>
      <Box sx={{ fontFamily: tokens.font.mono, fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
        {structure.total}
        <Box component="span" sx={{ fontSize: 14, fontWeight: 500, ml: 0.5, color: tokens.text.tertiary }}>
          {t('dashboard.donut.unit')}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.75 }}>
        <DonutSvg
          segments={segments}
          completedPct={completedPct}
          label={label}
          completedLabel={statusLabel(OrderStatusWire.Completed)}
        />
        <DonutLegend segments={segments} statusLabel={statusLabel} />
      </Box>
    </>
  );
};

export interface OrderStructureTileProps {
  data: AdminMetricsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn: string;
  gridRow?: string;
}

export const OrderStructureTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
  gridRow,
}: OrderStructureTileProps): ReactElement => {
  const { t } = useTranslation();
  const structure = data?.orderStructure;
  const label = t('dashboard.donut.title');

  return (
    <AnalyticsTile
      label={label}
      gridColumn={gridColumn}
      gridRow={gridRow}
      isLoading={isLoading}
      isError={isError}
      hasData={Boolean(structure)}
    >
      {structure ? <DonutBody structure={structure} label={label} /> : null}
    </AnalyticsTile>
  );
};
